import { useMemo, useState } from "react";
import { AlertTriangle, Download, Link2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChannelChip } from "@/components/common/Chips";
import { PrimaryBtn, OutlineBtn } from "@/components/common/ui";
import { tabCls } from "@/pages/Kasir";
import { cn } from "@/lib/utils";
import FilterTable from "./FilterTable";

const PF = { shopee: "Shopee", tokopedia: "Tokopedia/TikTok" };
const REASONS = ["beda tanggal", "belum diatur", "dibatalkan", "duplikat"];

function buildColumns(excluded) {
  const cols = [
    { key: "platform", label: "Platform", type: "text", token: (r) => PF[r.platform] || r.platform },
    { key: "order_id", label: "Order ID", type: "text", token: (r) => r.order_id || "", cellClass: "num" },
    {
      key: "master", label: "Master", type: "text", token: (r) => r.product_name || "",
      tokenLabel: (t) => (t === "" ? (excluded ? "(Tanpa master)" : "UNMAPPED") : t),
      cell: (r) => <span className={cn(!r.product_name && (excluded ? "text-zinc-400" : "font-bold text-brand"))}>{r.product_name || (excluded ? "—" : "UNMAPPED")}</span>,
    },
    {
      key: "raw_name", label: "Nama Produk (raw)", type: "text", token: (r) => r.raw_name || "",
      cell: (r) => <span className="block max-w-[260px] truncate" title={r.raw_name}>{r.raw_name}</span>,
    },
    { key: "raw_variation", label: "Nama Variasi", type: "text", token: (r) => r.raw_variation || "", cell: (r) => r.raw_variation || "-" },
    { key: "qty", label: "Qty", type: "num", align: "right", token: (r) => r.qty },
    { key: "ship_time", label: "Waktu Kirim", type: "datetime", token: (r) => r.ship_time || "", cell: (r) => r.ship_time || "-" },
    {
      key: "paid_time", label: "Waktu Bayar", type: "datetime", token: (r) => r.paid_time || "COD",
      cell: (r) => (r.paid_time ? r.paid_time : <span className="font-semibold">COD</span>),
    },
    { key: "status", label: "Status", type: "text", token: (r) => r.status || "", cell: (r) => <span className="block max-w-[140px] truncate" title={r.status}>{r.status}</span> },
    { key: "source_file", label: "File", type: "text", token: (r) => r.source_file || "", cell: (r) => <span className="block max-w-[140px] truncate" title={r.source_file}>{r.source_file}</span> },
  ];
  if (excluded)
    cols.push({
      key: "reason", label: "Alasan", type: "text", token: (r) => r.reason || "",
      cell: (r) => <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[11px] font-semibold">{r.reason}</span>,
    });
  return cols;
}

function Stats({ p }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Object.entries(p.platform_stats).map(([k, s]) => (
        <div key={k} className="rounded-xl border-2 border-ink bg-white p-4" data-testid={`import-stats-${k}`}>
          <ChannelChip channel={PF[k]} />
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[["Baris", s.lines, "lines"], ["Order", s.orders, "orders"], ["Qty", s.qty, "qty"]].map(([l, v, id]) => (
              <div key={l}><div className="text-[11px] uppercase tracking-wider text-zinc-500">{l}</div><div className="num text-2xl font-extrabold" data-testid={`import-${k}-${id}`}>{v}</div></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Unmapped({ rows, onMap }) {
  if (!rows.length) return null;
  return (
    <div className="rounded-xl border-2 border-brand bg-[#FDF1F1] p-4" data-testid="import-unmapped">
      <div className="mb-3 flex items-center gap-2 font-semibold text-brand"><AlertTriangle className="h-4 w-4" /> {rows.length} produk belum di-mapping — commit diblokir</div>
      <ul className="space-y-2">
        {rows.map((u, i) => (
          <li key={i} className="flex flex-col gap-2 rounded-lg bg-white p-3 sm:flex-row sm:items-center" data-testid={`unmapped-row-${i}`}>
            <ChannelChip channel={PF[u.platform]} />
            <div className="min-w-0 flex-1 text-sm"><div className="font-medium">{u.raw_name}</div><div className="text-xs text-zinc-500">Variasi: {u.raw_variation || "-"} · {u.lines} baris · {u.qty} qty</div></div>
            <OutlineBtn className="h-10" onClick={() => onMap(u)} data-testid={`unmapped-map-btn-${i}`}><Link2 className="h-4 w-4" /> Mapping</OutlineBtn>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Summary({ p }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white" data-testid="import-summary-table">
      <table className="dense-table w-full">
        <thead><tr><th>Produk Master</th><th>Kategori</th><th className="text-right">Shopee</th><th className="text-right">Tokopedia</th><th className="text-right">Total</th></tr></thead>
        <tbody>
          {p.summary.map((r) => (
            <tr key={r.product_id} data-testid={`summary-row-${r.code}`}>
              <td className="font-medium">{r.name}{r.is_bundle && <span className="ml-2 text-[11px] text-brand">→ 1 tiap varian</span>}{r.stock_unit === "lembar" && <span className="ml-2 text-[11px] text-zinc-500">pack ×{r.sales_pack_size}</span>}</td>
              <td>{r.category}</td><td className="num text-right">{r.shopee}</td><td className="num text-right">{r.tokopedia}</td><td className="num text-right font-bold">{r.total}</td>
            </tr>
          ))}
          {p.category_totals.map((c) => (
            <tr key={c.category} className="bg-zinc-50 font-semibold"><td>Total {c.category}</td><td /><td className="num text-right">{c.shopee}</td><td className="num text-right">{c.tokopedia}</td><td className="num text-right">{c.total}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PreviewView({ p, onMap, onCommit, onExport, onBack, committing }) {
  const [reason, setReason] = useState("");
  const [inclShown, setInclShown] = useState(p.included.length);
  const [exclShown, setExclShown] = useState(p.excluded.length);
  const inclCols = useMemo(() => buildColumns(false), []);
  const exclCols = useMemo(() => buildColumns(true), []);
  const exclExtra = reason ? (r) => r.reason === reason : undefined;

  return (
    <div className="space-y-6" data-testid="import-preview">
      <div className="flex flex-wrap gap-2">
        {p.files.map((f) => (
          <span key={f.name} className={cn("rounded-md border px-3 py-1.5 text-xs", f.error ? "border-brand text-brand" : "border-zinc-300 bg-white")} data-testid="import-file-chip">
            <b>{f.name}</b> · {f.error ? f.error : `${PF[f.platform]} · ${f.rows} baris`}
          </span>
        ))}
      </div>
      <Stats p={p} />
      <Unmapped rows={p.unmapped} onMap={onMap} />
      <Summary p={p} />
      <Tabs defaultValue="included">
        <TabsList className="mb-4 h-auto w-full justify-start gap-2 rounded-none border-b border-zinc-200 bg-transparent p-0">
          <TabsTrigger value="included" className={tabCls} data-testid="tab-included">Included ({inclShown} / {p.included.length})</TabsTrigger>
          <TabsTrigger value="excluded" className={tabCls} data-testid="tab-excluded">Excluded ({exclShown} / {p.excluded.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="included">
          <FilterTable testid="included-table" columns={inclCols} rows={p.included} onCount={setInclShown} />
        </TabsContent>
        <TabsContent value="excluded">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {["", ...REASONS].map((r) => (
              <button key={r || "all"} onClick={() => setReason(r)} data-testid={`excluded-filter-${r || "all"}`}
                className={cn("press rounded-full border px-3 py-1.5 text-xs font-semibold", reason === r ? "border-ink bg-ink text-white" : "border-zinc-300 bg-white")}>
                {r || "Semua"} ({r ? p.excluded.filter((x) => x.reason === r).length : p.excluded.length})
              </button>
            ))}
          </div>
          <FilterTable testid="excluded-table" columns={exclCols} rows={p.excluded} extraFilter={exclExtra} onCount={setExclShown} />
        </TabsContent>
      </Tabs>
      <div className="sticky bottom-20 z-20 flex flex-wrap gap-2 rounded-xl border-2 border-ink bg-white/95 p-3 backdrop-blur-md md:bottom-4">
        <OutlineBtn onClick={onBack} data-testid="import-back-btn">Kembali</OutlineBtn>
        <OutlineBtn onClick={onExport} data-testid="import-export-btn"><Download className="h-4 w-4" /> Export Excel</OutlineBtn>
        <PrimaryBtn className="ml-auto" onClick={onCommit} disabled={!p.can_commit || committing} data-testid="import-commit-btn">
          {committing ? "Menyimpan…" : p.committed ? "Sudah di-commit" : `Commit ${p.included.length} baris`}
        </PrimaryBtn>
      </div>
    </div>
  );
}
